import { useNavigate } from 'react-router-dom';
import { Dropdown, Flex, Select, Typography, notification } from 'antd';
import { useAuth } from '../../../context/AuthContext';
import { routesPath } from '../../constants/routesPath';
import React, { useState, useEffect } from 'react';
import Avatar from '../Avatar';
import { useTranslation } from 'react-i18next';
import { languages } from '../../constants/languages';
import classes from './UserIconDropdown.module.scss';
import icon from '../../styles/Icon.module.scss';
import { useUserService } from './../../../services/userService';
import { notificationType } from '../../constants/notificationType';
import { useReadOnly } from '../../../hooks/useReadOnly';

function UserIconDropdown() {
    const navigate = useNavigate();
    const { i18n, t } = useTranslation(['menu', 'impersonation']);
    // impersonating: no language sync (it would write to the impersonated user and to this browser's settings)
    const readOnly = useReadOnly();
    const { changeLanguage, getUserLanguage } = useUserService();
    const [userLang, setUserLang] = useState(null);

    const { logout, currentUser } = useAuth();

    const onClick = ({ key }) => {
        switch (key) {
            case 'profile':
                navigate(routesPath.ROOT.PROFILE);
                break;
            case 'settings':
                navigate(routesPath.ROOT.USER_SETTINGS);
                break;
            case 'logout':
                logout();
                break;
            default:
                break;
        }
    };

    const handleLanguageChange = async value => {
        try {
            await changeLanguage(value);
            i18n.changeLanguage(value);
        } catch {
            notification.open({
                type: notificationType.ERROR,
                message: 'Error',
                description: 'Failed to change language.',
                placement: 'top',
            });
        }
    };

    useEffect(() => {
        const fetchUserLanguage = async () => {
            const { data } = await getUserLanguage();
            setUserLang(data);
            i18n.changeLanguage(data);
        };

        if (!readOnly) {
            fetchUserLanguage();
        }
    }, []);

    const items = [
        {
            key: 'currentUsersEmail',
            label: <Typography.Text style={{ wordBreak: 'keep-all !important' }}>{currentUser?.email}</Typography.Text>,
        },

        { type: 'divider', key: 'divider1' },
        !readOnly && {
            key: 'language',
            label: (
                <Flex justify="space-between" align="center">
                    <Typography.Text>{t('language')}:</Typography.Text>
                    <Select
                        defaultValue={userLang || i18n.resolvedLanguage}
                        onClick={e => e.stopPropagation()}
                        onChange={value => handleLanguageChange(value)}
                        variant="filled"
                        dropdownStyle={{ minWidth: 100, width: 'fit-content' }}
                    >
                        {Object.values(languages).map(lng => (
                            <Select.Option key={lng.value} value={lng.value} disabled={i18n.language === lng.value}>
                                {lng.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Flex>
            ),
        },
        !readOnly && { type: 'divider', key: 'divider2' },
        {
            key: 'menu',
            label: t('menu'),
            disabled: true,
        },
        {
            key: 'profile',
            label: (
                <Flex align="center">
                    <i className={`fi fi-sr-user ${icon.md}`} />
                    <span className="ml-1">{t('profile')}</span>
                </Flex>
            ),
        },
        // personal settings: two-factor authentication lives there
        {
            key: 'settings',
            label: (
                <Flex align="center">
                    <i className={`fi fi-sr-settings ${icon.md}`} />
                    <span className="ml-1">{t('settings')}</span>
                </Flex>
            ),
        },
        { type: 'divider', key: 'divider3' },
        {
            key: 'logout',
            label: (
                <Flex align="center">
                    <i className={`fi fi-sr-exit ${icon.md}`} />
                    <span className="ml-1">{readOnly ? t('impersonation:overlay.stop') : t('log-out')}</span>
                </Flex>
            ),
        },
    ];

    return (
        <Dropdown
            menu={{ items: items.filter(Boolean), onClick }}
            placement="bottomRight"
            overlayClassName={classes['user-icon-dropdown-menu']}
            trigger={['click']}
        >
            <Avatar
                firstName={currentUser?.firstName}
                lastName={currentUser?.lastName}
                photoName={currentUser?.photo}
                controller={'users'}
                size={37}
                style={{
                    cursor: 'pointer',
                }}
            />
        </Dropdown>
    );
}

export default UserIconDropdown;
